<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="paymentTypes"
      :headers="tableHeaders"
      :list="paymentTypes"
      table-id="paymentTypes"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      clickable
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as PaymentType)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <PaymentTypeModalCreateEdit
      v-if="createEditModalActive"
      :paymentType="paymentType"
      :title="modalEditCreateTitle()"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { removePaymentTypesByIds, getPaymentTypes } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import PaymentTypeModalCreateEdit from '@/components/documents/PaymentTypeModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type PaymentType, ePaymentType } from '@/types/PaymentType';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { reloadLookups } = useLookupsStore();
const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const paymentTypes = ref<PaymentType[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'eTypeString', text: 'eType', type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializePaymentType(): PaymentType {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    eType: 0,
    eTypeString: '',
    isFromManager: true,
    isActive: false,
  };
}

onMounted(async () => {
  const res = await getPaymentTypes();
  paymentTypes.value = populatePaymentTypeField(res);
});

function getPaymentTypeString(eType: number): string {
  switch (eType) {
    case ePaymentType.Cash:
      return 'Cash';
    case ePaymentType.Credit:
      return 'Credit';
    case ePaymentType.CreditCard:
      return 'CreditCard';
    case ePaymentType.PaymentChannel:
      return 'PaymentChannel';
    case ePaymentType.Bank:
      return 'Bank';
    default:
      return 'Unknown';
  }
}

function populatePaymentTypeField(paymentTypes: PaymentType[]): PaymentType[] {
  return paymentTypes.map((paymentType) => ({
    ...paymentType,
    eTypeString: paymentType.eType != null ? getPaymentTypeString(paymentType.eType) : '',
  }));
}

async function onDeleteMultiselect(items: PaymentType[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removePaymentTypesByIds(idsToDelete);
  if (!res && paymentTypes.value)
    paymentTypes.value = paymentTypes.value.filter(
      (paymentType) => !idsToDelete.includes(paymentType.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['paymentTypes']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removePaymentTypesByIds([idToDelete]);
  if (!res && paymentTypes.value)
    paymentTypes.value = paymentTypes.value.filter((paymentType) => paymentType.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['paymentTypes']);
}

const paymentType = ref<PaymentType>(initializePaymentType());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNew() {
  paymentType.value = initializePaymentType();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEdit(item: PaymentType) {
  paymentType.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: PaymentType) {
  if (!paymentTypes.value) return;

  const index = paymentTypes.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) paymentTypes.value[index] = modifiedData;
  else paymentTypes.value.push(modifiedData);
  paymentTypes.value = populatePaymentTypeField(paymentTypes.value);
  createEditModalActive.value = false;

  await reloadLookups(['paymentTypes']);
}

async function refreshData(data: TableFilterData) {
  const response = await getPaymentTypes();
  let processedData = populatePaymentTypeField(response);

  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'eTypeString']);
  }

  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  paymentTypes.value = processedData;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<PaymentType>();
function onDeleteRow(item: PaymentType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (paymentType.value && !paymentType.value.isFromManager)
    return `${$t('common.actions.view')} Payment Type`;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} Payment Type`
    : `${$t('common.actions.create')} Payment Type`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
